import React, { useState, useEffect } from 'react';
import Tooltip from './Tooltip';
import { getAcronym } from './CityFeatures/Sections/Overview';
import { NoDataBox } from './UI/Charts/NoDataFoundMessage';


const useCountUp = (endValue, duration = 1500) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      
      setCount(Math.floor(endValue * percentage));

      if (progress < duration) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [endValue, duration]);

  return count;
};

// 2. Gauge Component
const Gauge = ({ label, value, color, acronymKey }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDisplayValue(value);
    }, 100); 
    return () => clearTimeout(timer);
  }, [value]);

  const radius = 40;
  const arcLength = Math.PI * radius; 
  const strokeDashoffset = arcLength - (arcLength * displayValue) / 100;
  const animatedNumber = useCountUp(value, 1500);

  return (
    <div className="flex flex-col items-center justify-end w-40">
      <div className="relative w-40 h-24 flex justify-center overflow-hidden">
        <svg width="160" height="80" viewBox="0 0 100 50" className="overflow-visible">
          {/* Gray Background Track */}
          <path
            d="M 10,50 A 40,40 0 0 1 90,50"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="10"
          />
          
          {/* Colored Animated Bar */}
          <path
            d="M 10,50 A 40,40 0 0 1 90,50"
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeDasharray={arcLength}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: "stroke-dashoffset 1.5s ease-out" }} 
          />
        </svg>

        <div 
          className="absolute bottom-0 text-xl font-bold" 
          style={{ color: color }}
        >
          {animatedNumber}%
        </div>
      </div>

      <p className="title-text-size text-[#2F4B7C] break-words text-center">
        {acronymKey ? <Tooltip id={acronymKey}>{label}</Tooltip> : label}
      </p>
    </div>
  );
};


const SdgIndicator = ({ data, isComparison }) => {

  // Helper function to assign colors
  const getColor = (indicatorName) => {
    switch (indicatorName) {
      case 'Safely managed': return "#15803d"; // Dark Green
      case 'Basic': return "#22c55e";          // Bright Green
      case 'Limited': return "#facc15";        // Yellow
      case 'Unimproved': return "#f97316";     // Orange
      case 'Open defecation': return "#ef4444";// Red
      default: return "#9ca3af";               // Default Gray
    }
  };

  return (
    <div className={`${isComparison ? "" : "rounded border-gray-100 2xl:flex items-center"}`}>
      
      <h3 className={`text-[#2F4B7C] text-lg my-2 text-start w-full  ${isComparison ? "" : "2xl:w-1/3"} `}>
        <Tooltip id="SDG">SDG</Tooltip> Indicator for Sanitation
      </h3>

      <div className={`flex flex-wrap ${isComparison ? " gap-4 md:gap-8 xl:gap-12" : " gap-10 xl:gap-16 "} flex-1 w-full`}>

        {data && data.length > 0 ? (
          data.map((item) => {
            // Added '|| 0' so it doesn't break if value is missing
            const numericValue = parseInt(item.value) || 0; 
            
            return (
              <Gauge 
                key={item.id}
                label={item.indicator}
                value={numericValue}
                color={getColor(item.indicator)}
                acronymKey={getAcronym(item.indicator)}
              />
            );
          })
        ) : (
         
          <div className="w-full flex justify-center items-center py-5">
            <NoDataBox />
          </div>
        )}
       
      </div>
    </div>
  );
};

export default SdgIndicator;